import React, { useState, useCallback, useEffect } from 'react';
import Collapsible from '../Collapsible';
import { FilterGroup } from './types/FilterGroup';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import { FilterGroupType } from './types/FilterGroupType';
import { FilterGroupOption } from './types/FilterGroupOption';
import FilterGroupElement from './FilterGroupElement';
import { text, fontFamilyBold } from '../../styling/mixins/typography';
import { useDispatch } from 'react-redux';
import { queryProductFiltering } from '../../Actions/ProductFiltering.action';
import styled from 'styled-components';
import { getColor } from '../../styling/mixins/colors';
import { breakpoint } from '../../styling/mixins/breakpoints';

const StyledCollapsible = styled(Collapsible)`
  & > label,
  button.toggle-collapsible {
    padding: 1.2rem 0;
    ${text('medium')}
    ${fontFamilyBold};
    display: flex;
    justify-content: space-between;
    width: 100%;
    ${breakpoint('medium', 'down')} {
      padding: 1.2rem 1.6rem;
    }
  }

  .collapsible__content .ReactCollapse--content {
    display: flex;
    flex-direction: column;
    padding: 0 0 1.6rem 0;

    & label {
      display: flex;
      justify-content: space-between;
    }
  }

  .show-all-button,
  .show-all-button:hover {
    border-color: ${getColor('grey', 300)};
    ${fontFamilyBold};
    letter-spacing: 0.5px;
    text-transform: uppercase;
    font-size: 1.1rem;
  }

  &:first-child {
    border-top: none;
  }
`;

const MAX_FILTERS_BEFORE_SHOW_MORE = 5;

const ProductFiltering = () => {
  const productFiltering = useTypedSelector((state) => state.productFiltering);
  const { filters } = productFiltering;
  const dispatch = useDispatch();

  const [showAllForFilters, setShowMoreForFilters] = useState<string[]>([]);
  const [filterSearchResults, setFilterSearchResults] = useState<{
    [filterId: string]: FilterGroupOption[];
  }>({});

  useEffect(() => {

    if (filters?.length > 0) return;

    // Fetch filters if none is in the state
    dispatch(queryProductFiltering(null, true));
  }, []);

  const onShowHideAllClick = (filterId: string) => {
    const listCopy = [...showAllForFilters];
    const index = listCopy.indexOf(filterId);
    if (index == -1) {
      listCopy.push(filterId);
    } else {
      listCopy.splice(index, 1);
    }
    setShowMoreForFilters(listCopy);
  };

  // Returns true if the filter has "show all" selected
  const shouldShowAllFilters = useCallback(
    (filterId: string) => showAllForFilters.indexOf(filterId) != -1,
    [showAllForFilters]
  );

  // Called when the search input was changed for the specified filter
  const onSearch = useCallback(
    (filter: FilterGroup, options: FilterGroupOption[]) => {
      setFilterSearchResults((current) => {
        const filterSearchResultsCopy = { ...current };
        if (!options) {
          if (filterSearchResultsCopy.hasOwnProperty(filter.id)) {
            delete filterSearchResultsCopy[filter.id];
          }
        } else {
          filterSearchResultsCopy[filter.id] = options;
        }

        return filterSearchResultsCopy ?? {};
      });
    },
    []
  );

  // Get options from the specified filter, with regard to the search value for the filter
  const getFilterOptions = useCallback(
    (filter: FilterGroup) =>
      filterSearchResults.hasOwnProperty(filter.id) &&
        filterSearchResults[filter.id] != undefined
        ? filterSearchResults[filter.id] ?? filter.options
        : filter.options,
    [filterSearchResults]
  );

  const getFilterGroupLabel = useCallback(
    (filter: FilterGroup) =>
      `${filter.filterGroupType != FilterGroupType.Range &&
        filter.selectedOptions.length > 0
        ? `(${filter.selectedOptions.length})`
        : ''
        } ${filter.label}`.trim(),
    []
  );

  return (
    <div>
      {filters?.length > 0
        ? filters.map((filter, idx) => (
          <StyledCollapsible
            isOpen={idx < 4 ? true : false}
            key={filter.id}
            label={getFilterGroupLabel(filter)}
          >
            <FilterGroupElement
              filter={filter}
              options={getFilterOptions(filter).slice(
                0,
                shouldShowAllFilters(filter.id)
                  ? undefined
                  : MAX_FILTERS_BEFORE_SHOW_MORE
              )}
              showAll={shouldShowAllFilters(filter.id)}
              onSearch={onSearch}
              onShowHideAllClick={onShowHideAllClick}
              showAllButtonVisible={
                getFilterOptions(filter).length > MAX_FILTERS_BEFORE_SHOW_MORE
              }
            />
          </StyledCollapsible>
        ))
        : null}
    </div>
  );
};

export default ProductFiltering;
